import { WEBSOCKET_BASE_URL } from '../config/api';
import { Notification } from './notificationApi';
import { ThreadMessageData, WebSocketMessage } from '../types/artist';

export type NotificationHandler = (notification: Notification) => void;
export type ThreadMessageHandler = (data: ThreadMessageData) => void;
export type ConnectionHandler = () => void;
export type ErrorHandler = (error: Event) => void;

/**
 * WebSocket client for real-time notifications
 * 
 * Note: In development mode with React Strict Mode enabled, you may see
 * connection attempts and disconnections in the console. This is expected
 * behavior as Strict Mode intentionally double-invokes effects to help
 * catch bugs. The client handles this gracefully and will establish a
 * stable connection after the initial mount/unmount cycle.
 */
export class NotificationWebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isIntentionallyClosed = false;
  private pingInterval: NodeJS.Timeout | null = null;

  private onNotificationHandlers: NotificationHandler[] = [];
  private onThreadMessageHandlers: ThreadMessageHandler[] = [];
  private onConnectHandlers: ConnectionHandler[] = [];
  private onDisconnectHandlers: ConnectionHandler[] = [];
  private onErrorHandlers: ErrorHandler[] = [];

  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    // Check if already connected or connecting
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    this.isIntentionallyClosed = false;
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      console.error('Cannot connect to WebSocket: No access token found');
      return;
    }

    // Use the configured WebSocket URL and add token as query parameter
    const wsUrl = `${WEBSOCKET_BASE_URL}/notifications?token=${encodeURIComponent(token)}`;
    
    try {
      console.log('Connecting to WebSocket:', WEBSOCKET_BASE_URL + '/notifications'); // Don't log the token
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.startPingInterval();
        this.onConnectHandlers.forEach(handler => handler());
      };

      this.ws.onmessage = (event) => {
        try {
          // Check for plain text messages (ping/pong)
          if (event.data === 'pong') {
            console.debug('Received pong from server');
            return;
          }

          // Try to parse as JSON
          const message: WebSocketMessage = JSON.parse(event.data);
          
          if (message.type === 'notification' && message.data) {
            // New notification received - transform PascalCase to camelCase
            const transformedNotification = {
              id: message.data.Id,
              message: message.data.Message,
              type: message.data.Type,
              timestamp: message.data.Timestamp,
              acknowledged: message.data.Acknowledged,
              actionUrl: message.data.ActionUrl,
              authorizedUserId: message.data.AuthorizedUserId,
            };
            console.log('Transformed notification:', transformedNotification);
            this.onNotificationHandlers.forEach(handler => handler(transformedNotification));
          } else if (message.type === 'thread_message' && message.data) {
            // New thread message received - transform PascalCase to camelCase
            console.log('Received thread message via WebSocket:', message.data);
            
            // Transform the message data from PascalCase to camelCase
            const transformedData = {
              feedbackId: message.data.feedbackId,
              threadId: message.data.threadId,
              message: {
                id: message.data.message.Id,
                feedbackThreadId: message.data.message.FeedbackThreadId,
                userId: message.data.message.UserId,
                message: message.data.message.Message,
                createdAt: message.data.message.CreatedAt,
              },
              trackTitle: message.data.trackTitle,
              trackPublicId: message.data.trackPublicId,
            };
            
            this.onThreadMessageHandlers.forEach(handler => handler(transformedData));
          } else if (message.type === 'connection') {
            // Connection acknowledgment
            console.log('WebSocket connection acknowledged:', message.message);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.onErrorHandlers.forEach(handler => handler(error));
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        this.stopPingInterval();
        this.onDisconnectHandlers.forEach(handler => handler());

        // Attempt to reconnect unless intentionally closed
        if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('Max reconnection attempts reached. Please refresh the page.');
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.stopPingInterval();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      // Only try to close if the connection is open or connecting
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        console.log('Disconnecting WebSocket');
        this.ws.close(1000, 'Client disconnecting');
      }
      this.ws = null;
    }
  }

  /**
   * Register a handler for incoming notifications
   */
  onNotification(handler: NotificationHandler): () => void {
    this.onNotificationHandlers.push(handler);
    // Return unsubscribe function
    return () => {
      this.onNotificationHandlers = this.onNotificationHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register a handler for incoming thread messages
   */
  onThreadMessage(handler: ThreadMessageHandler): () => void {
    this.onThreadMessageHandlers.push(handler);
    // Return unsubscribe function
    return () => {
      this.onThreadMessageHandlers = this.onThreadMessageHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register a handler for connection events
   */
  onConnect(handler: ConnectionHandler): () => void {
    this.onConnectHandlers.push(handler);
    return () => {
      this.onConnectHandlers = this.onConnectHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register a handler for disconnection events
   */
  onDisconnect(handler: ConnectionHandler): () => void {
    this.onDisconnectHandlers.push(handler);
    return () => {
      this.onDisconnectHandlers = this.onDisconnectHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register a handler for error events
   */
  onError(handler: ErrorHandler): () => void {
    this.onErrorHandlers.push(handler);
    return () => {
      this.onErrorHandlers = this.onErrorHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      return; // Already scheduled
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      console.log(`Reconnecting... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      this.connect();
    }, delay);
  }

  /**
   * Start sending periodic ping messages to keep connection alive
   */
  private startPingInterval(): void {
    this.stopPingInterval();
    
    // Send ping every 30 seconds
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send('ping');
        } catch (error) {
          console.error('Failed to send ping:', error);
        }
      }
    }, 30000);
  }

  /**
   * Stop sending ping messages
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

// Create a singleton instance
export const notificationWebSocket = new NotificationWebSocketClient();
